import { Component, Inject, Input, OnInit, Optional } from '@angular/core';
import { Router } from '@angular/router';
import { EnvironmentService } from '@galaxy/core';
import { AccountGroupApiService } from '@vendasta/account-group';
import { AppNamespace, OwnerType } from '@vendasta/email-builder';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { EMPTY, Observable, catchError, of, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import {
  AdvancedSections,
  DYNAMIC_COMPONENT_DATA_TOKEN,
  EMAIL_FOOTER_TOKEN,
  EMAIL_TOP_LOGO_TOKEN,
  EmailContentData,
  EmailPreviewHydrationData,
  HasUnsavedChangesGuard,
  PLACEHOLDER_LOGO_URL_TOKEN,
  TEMPLATE_HYDRATION_DATA_TOKEN,
} from '../../email-builder';
import { VariableMenuItem } from '../../email-builder/src/components/dynamic-component-selector';
import {
  Footer,
  TopLogo,
} from '../../email-builder/src/components/email-advanced-settings/email-advanced-settings.component';
import { EmailBuilderDataService } from '../../email-builder/src/services/email-builder-data.service';
import { EMAIL_LIBRARY_BASE_URL_TOKEN, PAGE_ROUTES } from './shared';

export interface BackUrlData {
  previousPageUrl: string;
  previousPageTitle: string;
}

const templateIdPrefix = 'TEMPLATE-';

@Component({
  selector: 'email-library-editor',
  templateUrl: './email-library-editor.component.html',
  styleUrls: ['./email-library-editor.component.scss'],
  standalone: false,
})
export class EmailLibraryEditorComponent implements OnInit {
  @Input()
  ownerID: string;
  @Input()
  ownerType: OwnerType;
  @Input() // Template ID: to create a new template, pass either null or "".
  templateID = '';
  @Input()
  backUrlData: BackUrlData = { previousPageUrl: '', previousPageTitle: '' };

  isNew = true;

  templateData$: Observable<EmailContentData> = EMPTY;
  htmlRendering = false;
  private dataForSaving: EmailContentData;
  private rawHtmlForSaving: string;

  public previewHydrationData$: Observable<EmailPreviewHydrationData>;

  readonly advancedSections: AdvancedSections = {
    topLogo: true,
    pageSetup: true,
    footer: true,
    colors: true,
  };

  constructor(
    private readonly dataService: EmailBuilderDataService,
    private readonly environmentService: EnvironmentService,
    private readonly accountGroups: AccountGroupApiService,
    private readonly router: Router,
    private readonly changesGuard: HasUnsavedChangesGuard,
    private readonly snackbarService: SnackbarService,
    @Inject(EMAIL_LIBRARY_BASE_URL_TOKEN) private readonly baseUrl$: Observable<string>,
    @Inject(PLACEHOLDER_LOGO_URL_TOKEN) @Optional() readonly placeholderLogo$: Observable<string>,
    @Inject(EMAIL_FOOTER_TOKEN) @Optional() private readonly footer$: Observable<Footer>,
    @Inject(EMAIL_TOP_LOGO_TOKEN) @Optional() readonly defaultTopLogo: TopLogo,
    @Inject(DYNAMIC_COMPONENT_DATA_TOKEN) readonly dynamicComponents: VariableMenuItem[],
    @Inject(TEMPLATE_HYDRATION_DATA_TOKEN)
    readonly initialPreviewHydrationData$: Observable<EmailPreviewHydrationData>,
  ) {}

  ngOnInit(): void {
    this.previewHydrationData$ = this.initialPreviewHydrationData$;
    const initialContent: EmailContentData = {
      subjectLine: '',
      previewText: '',
      id: this.templateID,
      emailSetup: {
        pageSetup: {
          collapsed: true,
        },
        topLogo: {
          showLogo: 'show',
          collapsed: true,
        },
        colors: {
          collapsed: true,
        },
        footer: {
          collapsed: true,
        },
      },
      emailContentBlocks: [],
    };
    const footer$ = this.footer$ ? this.footer$ : of(null);
    this.isNew = this.templateID === '';
    if (this.isNew) {
      this.templateID = templateIdPrefix + uuidv4();
      this.templateData$ = footer$.pipe(
        map((footer) => {
          if (footer) {
            initialContent.emailSetup.footer = footer;
          }
          if (this.defaultTopLogo?.defaultSourceUrl && initialContent.emailSetup.topLogo.showLogo === 'show') {
            initialContent.emailSetup.topLogo = { ...this.defaultTopLogo, ...initialContent.emailSetup.topLogo };
          }
          return initialContent;
        }),
      );
    } else {
      this.templateData$ = combineLatest([
        this.dataService.loadTemplate$(this.ownerType, this.ownerID, AppNamespace.EMAIL_LIBRARY, this.templateID),
        footer$,
      ]).pipe(
        map(([template, footer]) => {
          if (footer) {
            const disclaimer = template?.emailSetup?.footer?.disclaimer ?? '';
            template.emailSetup.footer = footer;
            template.emailSetup.footer.disclaimer = disclaimer;
          }
          return template;
        }),
      );
    }
  }

  setHtmlRendering($event: boolean): void {
    this.htmlRendering = $event;
  }

  stageDataForSaving($event: EmailContentData): void {
    this.changesGuard.markAsUnsaved();
    this.dataForSaving = $event;
  }

  stageRawHtmlForSaving($event: string): void {
    this.rawHtmlForSaving = $event;
  }

  saveAndClose(): void {
    if (this.dataForSaving) {
      const saveResult = this.isNew ? this.sendCreateRequest() : this.sendUpdateRequest();
      saveResult
        .pipe(
          catchError((err) => {
            this.snackbarService.openErrorSnack('EMAIL_BUILDER.SAVING.GENERIC_ERROR');
            throw new Error(err);
          }),
        )
        .subscribe((saveSuccessful) => {
          if (saveSuccessful) {
            this.changesGuard.markAsSaved();
            this.closePage();
            return;
          }
        });
    } else {
      this.closePage();
    }
  }

  closePage(): void {
    if (this.changesGuard.canNavigateAway) {
      if (this.isNew && this.dataForSaving) {
        this.baseUrl$.pipe(take(1)).subscribe((baseUrl) => {
          const userAddedTemplate = {
            title: this.dataForSaving?.name,
            created: new Date(),
            editorUrl: baseUrl + PAGE_ROUTES.ROOT.TEMPLATE_SUBROUTES.EDIT(this.templateID),
            templateId: this.templateID,
            templateJson: '',
            templateSubject: this.dataForSaving?.subjectLine,
            templateHtml: this.rawHtmlForSaving,
            updated: new Date(),
          };
          this.router.navigateByUrl(this.backUrlData.previousPageUrl, {
            state: {
              template: userAddedTemplate,
            },
          });
        });
      } else {
        this.router.navigateByUrl(this.backUrlData.previousPageUrl);
      }
    } else {
      const dialog = this.changesGuard.showUnsavedChangesDialog();
      dialog.afterClosed().subscribe((result) => {
        if (result?.navigateAwayAndDiscard) {
          this.router.navigateByUrl(this.backUrlData.previousPageUrl);
        }
      });
    }
  }

  private sendUpdateRequest(): Observable<boolean> {
    return this.dataService.updateExistingTemplate$(
      this.ownerType,
      this.ownerID,
      AppNamespace.EMAIL_LIBRARY,
      this.templateID,
      this.dataForSaving,
      this.rawHtmlForSaving,
    );
  }

  private sendCreateRequest(): Observable<boolean> {
    return this.dataService.createNewTemplate$(
      this.ownerType,
      this.ownerID,
      AppNamespace.EMAIL_LIBRARY,
      this.templateID,
      this.dataForSaving,
      this.rawHtmlForSaving,
    );
  }
}
